// The dashboard's tabs ("All open", "Mine", "Team", …) as pure rules.
//
// Single source of truth for which ticket belongs under which tab. The SQL
// function search_tickets_v2 (migrations 076 + 080) computes the same flags
// server-side so search results and tab counts agree with the normal list.
// If you change a rule here, change the g_* flags in the live function too
// (the latest definition is in 080_fyi_lane.sql).
//
// Pure on purpose (no React, no Supabase, no DOM): tests/ticket-groups.test.ts
// imports it directly.

export type GroupKey =
  | 'open'
  | 'mine'
  | 'team'
  | 'unassigned'
  | 'fyi'
  | 'watching'
  | 'snoozed'
  | 'all'
  | 'archived';

export const GROUP_KEYS: readonly GroupKey[] = [
  'open',
  'mine',
  'team',
  'unassigned',
  'fyi',
  'watching',
  'snoozed',
  'all',
  'archived',
];

export const GROUPS: Array<{ key: GroupKey; label: string }> = [
  { key: 'open', label: 'All open' },
  { key: 'mine', label: 'Mine' },
  { key: 'team', label: 'Team' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'fyi', label: 'FYI' },
  { key: 'watching', label: 'Watching' },
  { key: 'snoozed', label: 'Snoozed' },
  { key: 'all', label: 'All' },
  { key: 'archived', label: 'Archived' },
];

export const CLOSED_STATUSES: ReadonlySet<string> = new Set(['resolved', 'closed']);

/** The few ticket fields the tab rules read. Structural so both the
 *  full `Ticket` row and a search-result ticket satisfy it. */
export interface GroupableTicket {
  id: string;
  status?: string | null;
  assignee_slack_id?: string | null;
  archived_at?: string | null;
  snoozed_until?: string | null;
  /** Robot mail (migration 080): a flagged sender nobody replied to or took on.
   *  Missing / null / false all mean "not robot mail". */
  is_automated_sender?: boolean | null;
}

/** Unknown / missing values fall back to the default tab. */
export function parseGroupKey(value: string | null | undefined): GroupKey {
  return (GROUP_KEYS as readonly string[]).includes(value ?? '')
    ? (value as GroupKey)
    : 'open';
}

/** Whether the ticket is inside its snooze window: `snoozed_until` exists
 *  AND hasn't passed. An expired snooze counts as un-snoozed (the
 *  auto-resurface cron clears the column within ~5 minutes). */
export function isSnoozedNow(
  ticket: Pick<GroupableTicket, 'snoozed_until'>,
  now: number = Date.now(),
): boolean {
  if (!ticket.snoozed_until) return false;
  return new Date(ticket.snoozed_until).getTime() > now;
}

/**
 * Archived tickets are hidden from every tab except "Archived"; snoozed ones
 * from every tab except "Snoozed". Archived wins over snoozed.
 *
 * Robot mail has its own lane: while it is open it shows under "FYI" (and "All",
 * and "Watching" if someone watches it) and NOT under All open / Mine / Team /
 * Unassigned, so the working lists hold only mail a person has to act on.
 */
export function matchesGroup(
  ticket: GroupableTicket,
  group: GroupKey,
  mySlackId: string | null,
  watchedIds: ReadonlySet<string>,
  now: number = Date.now(),
): boolean {
  const isArchived = !!ticket.archived_at;
  if (group === 'archived') return isArchived;
  if (isArchived) return false;

  const snoozed = isSnoozedNow(ticket, now);
  if (group === 'snoozed') return snoozed;
  if (snoozed) return false;

  const robot = ticket.is_automated_sender === true;
  switch (group) {
    case 'open':
      return !robot && !CLOSED_STATUSES.has(ticket.status ?? '');
    case 'mine':
      return !robot && !!mySlackId && ticket.assignee_slack_id === mySlackId;
    case 'team':
      return !robot && !!ticket.assignee_slack_id && ticket.assignee_slack_id !== mySlackId;
    case 'unassigned':
      return !robot && !ticket.assignee_slack_id;
    case 'fyi':
      return robot && !CLOSED_STATUSES.has(ticket.status ?? '');
    case 'watching':
      // Membership lives in ticket_watchers, not on the ticket row. Resolved
      // or closed watched tickets drop off the tab.
      return watchedIds.has(ticket.id) && !CLOSED_STATUSES.has(ticket.status ?? '');
    case 'all':
      return true;
  }
}
